<template>
	<div>
		<!-- Breed-standard benchmark + projection -->
		<div v-if="benchmark" class="bench">
			<div class="bench-head">
				<span class="bench-breed">vs {{ benchmark.breed }}</span>
				<span class="bench-age">Day {{ benchmark.ageDays }} · wk {{ benchmark.ageWeeks }}</span>
			</div>
			<div class="bench-items">
				<div v-for="it in benchmark.items" :key="it.label" class="bench-item">
					<span class="bench-dot" :class="'dot-' + it.color"></span>
					<div>
						<div class="bench-label">{{ it.label }}</div>
						<div class="bench-val">
							{{ it.actual }}
							<span class="bench-target">/ {{ it.target }}<template v-if="it.pct != null"> · {{ it.pct }}%</template></span>
						</div>
					</div>
				</div>
			</div>
			<div v-if="benchmark.projection" class="bench-proj">
				<feather-icon icon="TrendingUpIcon" size="14" />
				<span>{{ benchmark.projection }}</span>
			</div>
		</div>

		<!-- Granularity toggle + breadcrumb -->
		<div class="drill-toolbar">
			<div class="drill-seg">
				<button type="button" :class="{ active: view === 'month' }" @click="setView('month')">Monthly</button>
				<button type="button" :class="{ active: view === 'week' }" @click="setView('week')">Weekly</button>
				<button type="button" :class="{ active: view === 'day' }" @click="setView('day')">Daily</button>
			</div>
			<div class="drill-crumb" v-if="crumbLabel">
				<a href="#" @click.prevent="crumbBack">{{ crumbRoot }}</a>
				<feather-icon icon="ChevronRightIcon" size="14" />
				<span>{{ crumbLabel }}</span>
			</div>
		</div>

		<!-- Monthly / Weekly cards -->
		<div v-if="view === 'month' || view === 'week'" class="drill-grid">
			<div
				v-for="grp in view === 'month' ? monthGroups : visibleWeeks"
				:key="grp.key"
				class="drill-card"
				@click="view === 'month' ? openMonth(grp) : openWeek(grp)"
			>
				<div class="drill-card-head">
					<div>
						<div class="drill-card-title">{{ grp.title }}</div>
						<div class="drill-card-sub">{{ grp.subtitle }}</div>
					</div>
					<feather-icon icon="ChevronRightIcon" size="18" class="drill-chevron" />
				</div>
				<div class="drill-spark" v-if="grp.spark">
					<span class="drill-spark-label">{{ sparkLabel }}</span>
					<svg viewBox="0 0 100 30" preserveAspectRatio="none" class="drill-spark-svg">
						<polyline :points="grp.spark" fill="none" stroke="#296DB4" stroke-width="1.6" vector-effect="non-scaling-stroke" stroke-linejoin="round" stroke-linecap="round" />
					</svg>
				</div>
				<div class="drill-stats">
					<div class="drill-stat" v-for="s in grp.stats" :key="s.label">
						<div class="drill-stat-ic" :style="{ background: s.tint, color: s.color }">
							<feather-icon :icon="s.icon" size="15" />
						</div>
						<div>
							<div class="drill-stat-label">{{ s.label }}</div>
							<div class="drill-stat-val">{{ s.value }}</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Daily table (unchanged b-table, same fields) -->
		<div v-else>
			<b-table
				:items="visibleDays"
				responsive
				:fields="dailyFields"
				show-empty
				empty-text="No matching records found"
				class="position-relative"
			/>
		</div>
	</div>
</template>

<script>
import { BTable } from "bootstrap-vue";
import moment from "moment";

export default {
	name: "FlockReportDrilldown",
	components: { BTable },
	props: {
		// The daily report array already fetched by the parent (getCurrentReport)
		rows: { type: Array, default: () => [] },
		// The b-table fields the parent already built per production type
		fields: { type: Array, default: () => [] },
		// 1 = broiler, 2 = breeder, 3 = layer
		productionType: { type: [Number, String], default: 1 },
	},
	data() {
		return {
			view: "month",
			activeMonthKey: null,
			activeWeekIdx: null,
		};
	},
	created() {
		// Default to monthly for long cycles, daily for young flocks — a 3-day-old
		// flock has nothing to roll up.
		this.view = this.rows.length > 45 ? "month" : "day";
	},
	watch: {
		// New flock / new date range selected upstream → reset the drill state.
		rows() {
			this.activeMonthKey = null;
			this.activeWeekIdx = null;
			this.view = this.rows.length > 45 ? "month" : "day";
		},
	},
	computed: {
		// Age = position in the chronological array (day 0, 1, 2 …); matches the
		// Age column the daily table already shows.
		indexedRows() {
			return this.rows.map((r, i) => ({ ...r, _age: i }));
		},
		dailyFields() {
			// Reuse the parent's exact columns; the old "Age" used the b-table row
			// index, so map it to our real _age so subsets stay correct.
			return this.fields.map((f) => (f.key === "index" ? { key: "_age", label: f.label } : f));
		},
		metricConfig() {
			const pt = Number(this.productionType);
			if (pt === 2) {
				// Breeder
				return [
					{ label: "Eggs", key: "egg_produced", agg: "sum", dec: 0, icon: "CircleIcon", color: "#296DB4" },
					{ label: "HDEP", key: "hdep", agg: "avg", dec: 1, suffix: " %", icon: "PercentIcon", color: "#28C76F" },
					{ label: "HHEP", key: "hhep", agg: "avg", dec: 1, suffix: " %", icon: "PercentIcon", color: "#00CFE8" },
					{ label: "Hatching", key: "hatch", agg: "saleable", dec: 1, suffix: " %", icon: "CheckCircleIcon", color: "#7367F0" },
					{ label: "Eggs/HH", key: "eggshh", agg: "eggshh", dec: 1, icon: "HomeIcon", color: "#FF9F43" },
					{ label: "Mortality", key: "mortality_rate", agg: "sum", dec: 2, suffix: " %", icon: "TrendingDownIcon", color: "#EA5455" },
				];
			}
			if (pt === 3) {
				// Layer
				return [
					{ label: "Eggs", key: "egg_produced", agg: "sum", dec: 0, icon: "CircleIcon", color: "#296DB4" },
					{ label: "HDEP", key: "hdep", agg: "avg", dec: 1, suffix: " %", icon: "PercentIcon", color: "#28C76F" },
					{ label: "HHEP", key: "hhep", agg: "avg", dec: 1, suffix: " %", icon: "PercentIcon", color: "#00CFE8" },
					{ label: "Egg mass", key: "eggmass", agg: "eggmass", dec: 1, suffix: " g", icon: "BoxIcon", color: "#7367F0" },
					{ label: "Layer FCR", key: "lfcr", agg: "layerfcr", dec: 2, icon: "ActivityIcon", color: "#FF9F43" },
					{ label: "Saleable", key: "sale", agg: "saleable", dec: 1, suffix: " %", icon: "CheckCircleIcon", color: "#28C76F" },
					{ label: "Mortality", key: "mortality_rate", agg: "sum", dec: 2, suffix: " %", icon: "TrendingDownIcon", color: "#EA5455" },
				];
			}
			// Broiler
			return [
				{ label: "EPEF", key: "epef", agg: "epef", dec: 0, icon: "AwardIcon", color: "#296DB4" },
				{ label: "FCR", key: "fcr", agg: "fcr", dec: 2, icon: "ActivityIcon", color: "#00CFE8" },
				{ label: "ADG", key: "adg", agg: "adg", dec: 0, suffix: " g/d", icon: "TrendingUpIcon", color: "#28C76F" },
				{ label: "Livability", key: "liv", agg: "livability", dec: 1, suffix: " %", icon: "HeartIcon", color: "#7367F0" },
				{ label: "Water:Feed", key: "wf", agg: "waterfeed", dec: 2, icon: "DropletIcon", color: "#FF9F43" },
				{ label: "Mortality", key: "mortality_rate", agg: "sum", dec: 2, suffix: " %", icon: "TrendingDownIcon", color: "#EA5455" },
			];
		},
		// Which daily metric each card's sparkline traces
		sparkKey() {
			const pt = Number(this.productionType);
			return pt === 2 || pt === 3 ? "egg_produced" : "body_weight";
		},
		sparkLabel() {
			const pt = Number(this.productionType);
			return pt === 2 || pt === 3 ? "Egg production" : "Body weight";
		},
		// Breed-standard benchmark + harvest/lay projection, from the latest data.
		benchmark() {
			const rows = this.indexedRows;
			if (!rows.length) return null;
			const last = rows[rows.length - 1];
			const breed = last.benchmark_breed;
			if (!breed) return null;

			const pt = Number(this.productionType);
			const ageDays = Number(last._age) || 0;
			const housed = this.num(last.total_birds);
			const alive = this.num(last.alive_birds) || housed;
			const items = [];

			// higher-is-better helper: colour by how actual compares to target
			const grade = (actual, target, betterLow) => {
				if (!target || !actual) return "muted";
				const ratio = actual / target;
				if (betterLow) return ratio <= 1 ? "good" : ratio <= 1.1 ? "warn" : "bad";
				return ratio >= 1 ? "good" : ratio >= 0.9 ? "warn" : "bad";
			};

			if (pt === 1) {
				const bw = this.num(last.body_weight), tbw = this.num(last.target_body_weight);
				if (tbw) items.push({ label: "Body weight", actual: Math.round(bw) + " g", pct: bw && tbw ? Math.round((bw / tbw) * 100) : null, target: Math.round(tbw) + " g", color: grade(bw, tbw, false) });
				const feedKg = this.num(last.total_feed_consumption), fcr = bw > 0 && alive > 0 ? feedKg / ((bw / 1000) * alive) : 0, tfcr = this.num(last.target_fcr);
				if (tfcr) items.push({ label: "FCR", actual: fcr ? fcr.toFixed(2) : "—", pct: fcr && tfcr ? Math.round((tfcr / fcr) * 100) : null, target: tfcr.toFixed(2), color: grade(fcr, tfcr, true) });
				const mort = housed > 0 ? ((housed - alive) / housed) * 100 : 0, tmort = this.num(last.target_cumulative_mortality);
				if (tmort) items.push({ label: "Mortality", actual: mort.toFixed(1) + " %", pct: null, target: tmort.toFixed(1) + " %", color: grade(mort, tmort, true) });
			} else {
				const recent = rows.slice(-7).map((r) => this.num(r.hdep)).filter((x) => x > 0);
				const hdep = recent.length ? recent.reduce((s, x) => s + x, 0) / recent.length : 0, thdep = this.num(last.target_hdep);
				if (thdep) items.push({ label: "HDEP", actual: hdep.toFixed(1) + " %", pct: hdep && thdep ? Math.round((hdep / thdep) * 100) : null, target: thdep.toFixed(1) + " %", color: grade(hdep, thdep, false) });
				const ew = this.num(last.egg_weight), tew = this.num(last.target_egg_weight);
				if (tew && ew) items.push({ label: "Egg weight", actual: ew.toFixed(1) + " g", pct: Math.round((ew / tew) * 100), target: tew.toFixed(1) + " g", color: grade(ew, tew, false) });
				const mort = housed > 0 ? ((housed - alive) / housed) * 100 : 0, tmort = this.num(last.target_cumulative_mortality);
				if (tmort) items.push({ label: "Mortality", actual: mort.toFixed(1) + " %", pct: null, target: tmort.toFixed(1) + " %", color: grade(mort, tmort, true) });
			}

			// Uniformity (CV%) — only when sample weights were captured
			const cvRows = rows.map((r) => this.num(r.cv_percent)).filter((x) => x > 0);
			if (cvRows.length) {
				const cv = cvRows[cvRows.length - 1];
				items.push({ label: "Uniformity (CV)", actual: cv.toFixed(1) + " %", pct: null, target: "<10%", color: cv < 10 ? "good" : cv < 12 ? "warn" : "bad" });
			}

			// Projection
			let projection = null;
			if (pt === 1) {
				// broiler: ADG over last week → days to target weight
				const wRows = rows.filter((r) => this.num(r.body_weight) > 0);
				if (wRows.length >= 2) {
					const a = wRows[Math.max(0, wRows.length - 8)], b = wRows[wRows.length - 1];
					const days = (Number(b._age) - Number(a._age)) || 1;
					const adg = (this.num(b.body_weight) - this.num(a.body_weight)) / days;
					const bw = this.num(b.body_weight), tbw = this.num(last.target_body_weight);
					if (bw >= tbw && tbw) projection = "Market weight (" + Math.round(tbw) + " g) reached at day " + ageDays + ".";
					else if (adg > 0 && tbw) { const n = Math.ceil((tbw - bw) / adg); projection = "At ~" + Math.round(adg) + " g/day, reaches " + Math.round(tbw) + " g target in ~" + n + " days (age " + (ageDays + n) + ")."; }
				}
			} else {
				const thdep = this.num(last.target_hdep);
				const recent = rows.slice(-7).map((r) => this.num(r.hdep)).filter((x) => x > 0);
				const hdep = recent.length ? recent.reduce((s, x) => s + x, 0) / recent.length : 0;
				if (thdep && hdep) projection = hdep >= thdep * 0.98 ? "At/near breed peak lay (" + thdep.toFixed(0) + "%)." : "Laying at " + hdep.toFixed(0) + "% — breed target ~" + thdep.toFixed(0) + "% at this age.";
			}

			return { breed, ageDays, ageWeeks: Math.floor(ageDays / 7), items, projection };
		},
		monthGroups() {
			const map = {};
			this.indexedRows.forEach((r) => {
				const key = String(r.date).slice(0, 7);
				(map[key] = map[key] || []).push(r);
			});
			return Object.keys(map).map((key) => {
				const g = map[key];
				return {
					key,
					rows: g,
					title: moment(key + "-01").format("MMMM YYYY"),
					subtitle: this.rangeLabel(g),
					stats: this.buildStats(g),
					spark: this.sparkPoints(g),
				};
			});
		},
		weekGroups() {
			const out = [];
			for (let i = 0; i * 7 < this.indexedRows.length; i++) {
				const g = this.indexedRows.slice(i * 7, i * 7 + 7);
				out.push({
					key: "w" + i,
					idx: i,
					rows: g,
					title: "Week " + (i + 1),
					subtitle: this.rangeLabel(g),
					stats: this.buildStats(g),
					spark: this.sparkPoints(g),
				});
			}
			return out;
		},
		visibleWeeks() {
			if (!this.activeMonthKey) return this.weekGroups;
			return this.weekGroups.filter((w) => w.rows.some((r) => String(r.date).slice(0, 7) === this.activeMonthKey));
		},
		visibleDays() {
			if (this.activeWeekIdx != null && this.weekGroups[this.activeWeekIdx]) {
				return this.weekGroups[this.activeWeekIdx].rows;
			}
			return this.indexedRows;
		},
		crumbRoot() {
			return this.view === "week" ? "Months" : "Weeks";
		},
		crumbLabel() {
			if (this.view === "week" && this.activeMonthKey) {
				return moment(this.activeMonthKey + "-01").format("MMMM YYYY");
			}
			if (this.view === "day" && this.activeWeekIdx != null) {
				return "Week " + (this.activeWeekIdx + 1);
			}
			return "";
		},
	},
	methods: {
		num(v) {
			return parseFloat(String(v == null ? 0 : v).replace(/,/g, "")) || 0;
		},
		rangeLabel(g) {
			if (!g.length) return "";
			const a = g[0], b = g[g.length - 1];
			return "Age " + a._age + "–" + b._age + " d · " + moment(a.date).format("D MMM") + "–" + moment(b.date).format("D MMM");
		},
		buildStats(g) {
			// Shared period figures (housed = original placement, last = period end)
			const housed = this.num(g[0] && g[0].total_birds);
			const last = g[g.length - 1] || {};
			const lastAlive = this.num(last.alive_birds) || housed;
			const bwG = this.num(last.body_weight);
			const bwKg = bwG / 1000;
			const age = Number(last._age) || 0;
			const feedKgEnd = this.num(last.total_feed_consumption);
			const dash = "—";

			const fcr = () => (bwKg > 0 && lastAlive > 0 ? feedKgEnd / (bwKg * lastAlive) : 0);

			return this.metricConfig.map((m) => {
				let display;
				const fmt = (val) => (m.dec ? val.toFixed(m.dec) + (m.suffix || "") : Math.round(val).toLocaleString() + (m.suffix || ""));

				switch (m.agg) {
					case "fcr": {
						const v = fcr();
						display = v > 0 ? v.toFixed(2) : dash;
						break;
					}
					case "epef": {
						// (livability% × live-weight kg) ÷ (age days × FCR) × 100
						const f = fcr();
						const liv = housed > 0 ? (lastAlive / housed) * 100 : 0;
						const v = age > 0 && f > 0 && bwKg > 0 ? (liv * bwKg) / (age * f) * 100 : 0;
						display = v > 0 ? Math.round(v).toLocaleString() : dash;
						break;
					}
					case "adg": {
						const v = age > 0 && bwG > 0 ? bwG / age : 0;
						display = v > 0 ? fmt(v) : dash;
						break;
					}
					case "livability": {
						const v = housed > 0 ? (lastAlive / housed) * 100 : 0;
						display = fmt(v);
						break;
					}
					case "waterfeed": {
						const wSum = g.reduce((s, r) => s + this.num(r.water), 0);
						const fSum = g.reduce((s, r) => s + this.num(r.feed_consumption), 0);
						const v = fSum > 0 ? wSum / fSum : 0;
						display = v > 0 ? v.toFixed(2) : dash;
						break;
					}
					case "eggmass": {
						// avg g/bird/day = mean of (eggs × egg-weight ÷ live hens) over laying days
						const vals = g
							.map((r) => {
								const e = this.num(r.egg_produced), w = this.num(r.egg_weight), a = this.num(r.alive_birds);
								return e > 0 && a > 0 && w > 0 ? (e * w) / a : null;
							})
							.filter((x) => x != null);
						const v = vals.length ? vals.reduce((s, x) => s + x, 0) / vals.length : 0;
						display = v > 0 ? fmt(v) : dash;
						break;
					}
					case "layerfcr": {
						// feed (kg) ÷ egg mass (kg)
						const fSum = g.reduce((s, r) => s + this.num(r.feed_consumption), 0);
						const massKg = g.reduce((s, r) => s + (this.num(r.egg_produced) * this.num(r.egg_weight)) / 1000, 0);
						const v = massKg > 0 ? fSum / massKg : 0;
						display = v > 0 ? v.toFixed(2) : dash;
						break;
					}
					case "saleable": {
						// usable ÷ produced × 100 (breeder: hatching-egg %, layer: saleable %)
						const usable = g.reduce((s, r) => s + this.num(r.usable_eggs), 0);
						const produced = g.reduce((s, r) => s + this.num(r.egg_produced), 0);
						display = produced > 0 && usable > 0 ? fmt((usable / produced) * 100) : dash;
						break;
					}
					case "eggshh": {
						// eggs per hen housed for the period
						const eggs = g.reduce((s, r) => s + this.num(r.egg_produced), 0);
						const v = housed > 0 ? eggs / housed : 0;
						display = v > 0 ? fmt(v) : dash;
						break;
					}
					case "avg": {
						const vals = g.map((r) => this.num(r[m.key])).filter((x) => x > 0);
						const v = vals.length ? vals.reduce((s, x) => s + x, 0) / vals.length : 0;
						display = fmt(v);
						break;
					}
					default: {
						const v = g.reduce((s, r) => s + this.num(r[m.key]), 0);
						display = fmt(v);
					}
				}
				return { label: m.label, value: display, icon: m.icon, color: m.color, tint: this.tint(m.color) };
			});
		},
		// Build a tiny SVG polyline (0..100 x, 0..30 y) tracing the period's daily
		// values of the primary metric. Returns null when there's nothing to draw.
		sparkPoints(g) {
			const vals = g.map((r) => this.num(r[this.sparkKey]));
			if (vals.length < 2 || vals.every((v) => v === 0)) return null;
			const max = Math.max(...vals), min = Math.min(...vals);
			const H = 30, W = 100, pad = 3, span = max - min || 1;
			const pts = vals.map((v, i) => {
				const x = (i / (vals.length - 1)) * W;
				const y = H - pad - ((v - min) / span) * (H - pad * 2);
				return x.toFixed(1) + "," + y.toFixed(1);
			});
			return pts.join(" ");
		},
		tint(hex) {
			const r = parseInt(hex.slice(1, 3), 16), g = parseInt(hex.slice(3, 5), 16), b = parseInt(hex.slice(5, 7), 16);
			return "rgba(" + r + "," + g + "," + b + ",0.12)";
		},
		setView(v) {
			this.view = v;
			this.activeMonthKey = null;
			this.activeWeekIdx = null;
		},
		openMonth(m) {
			this.activeMonthKey = m.key;
			this.view = "week";
		},
		openWeek(w) {
			this.activeWeekIdx = w.idx;
			this.view = "day";
		},
		crumbBack() {
			if (this.view === "week") {
				this.view = "month";
				this.activeMonthKey = null;
			} else if (this.view === "day") {
				this.view = "week";
				this.activeWeekIdx = null;
			}
		},
	},
};
</script>

<style lang="scss" scoped>
.bench {
	margin: 0 1.5rem 1rem;
	padding: 14px 16px;
	background: #fff;
	border: 1px solid #ebe9f1;
	border-left: 3px solid #296db4;
	border-radius: 6px;
}
.bench-head {
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 10px;
}
.bench-breed {
	font-size: 13px;
	font-weight: 500;
	color: #296db4;
}
.bench-age {
	font-size: 12px;
	color: #b9b9c3;
}
.bench-items {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
	gap: 12px;
}
.bench-item {
	display: flex;
	align-items: center;
	gap: 8px;
}
.bench-dot {
	width: 9px;
	height: 9px;
	border-radius: 50%;
	flex: 0 0 auto;
}
.dot-good {
	background: #28c76f;
}
.dot-warn {
	background: #ff9f43;
}
.dot-bad {
	background: #ea5455;
}
.dot-muted {
	background: #d8d6de;
}
.bench-label {
	font-size: 11px;
	color: #b9b9c3;
	line-height: 1.2;
}
.bench-val {
	font-size: 14px;
	font-weight: 500;
	color: #5e5873;
}
.bench-target {
	font-size: 12px;
	font-weight: 400;
	color: #b9b9c3;
}
.bench-proj {
	display: flex;
	align-items: center;
	gap: 6px;
	margin-top: 10px;
	padding-top: 10px;
	border-top: 1px solid #f3f2f7;
	font-size: 12.5px;
	color: #6e6b7b;
}
.drill-toolbar {
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-wrap: wrap;
	gap: 10px;
	padding: 0 1.5rem 1rem;
}
.drill-seg {
	display: inline-flex;
	background: #f3f2f7;
	border-radius: 8px;
	padding: 3px;
	gap: 3px;
	button {
		border: none;
		background: transparent;
		border-radius: 6px;
		padding: 7px 16px;
		font-size: 13px;
		color: #6e6b7b;
		cursor: pointer;
		transition: all 0.2s;
		&.active {
			background: #296db4;
			color: #fff;
			font-weight: 500;
		}
	}
}
.drill-crumb {
	display: flex;
	align-items: center;
	gap: 6px;
	font-size: 13px;
	color: #b9b9c3;
	a {
		color: #296db4;
		text-decoration: none;
	}
}
.drill-grid {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
	gap: 14px;
	padding: 0 1.5rem 1.5rem;
}
.drill-card {
	background: #fff;
	border: 1px solid #ebe9f1;
	border-radius: 8px;
	padding: 14px 16px;
	cursor: pointer;
	transition: box-shadow 0.2s, border-color 0.2s;
	&:hover {
		border-color: #296db4;
		box-shadow: 0 4px 18px 0 rgba(41, 109, 180, 0.18);
	}
}
.drill-card-head {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
}
.drill-card-title {
	font-size: 15px;
	font-weight: 500;
	color: #5e5873;
}
.drill-card-sub {
	font-size: 12px;
	color: #b9b9c3;
	margin-top: 2px;
}
.drill-chevron {
	color: #b9b9c3;
	flex: 0 0 auto;
}
.drill-spark {
	margin-top: 12px;
	position: relative;
}
.drill-spark-label {
	font-size: 10px;
	text-transform: uppercase;
	letter-spacing: 0.4px;
	color: #b9b9c3;
}
.drill-spark-svg {
	display: block;
	width: 100%;
	height: 34px;
	margin-top: 2px;
}
.drill-stats {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(88px, 1fr));
	gap: 10px 12px;
	margin-top: 12px;
}
.drill-stat {
	display: flex;
	align-items: center;
	gap: 8px;
}
.drill-stat-ic {
	width: 30px;
	height: 30px;
	border-radius: 7px;
	display: flex;
	align-items: center;
	justify-content: center;
	flex: 0 0 auto;
}
.drill-stat-label {
	font-size: 11px;
	color: #b9b9c3;
	line-height: 1.2;
}
.drill-stat-val {
	font-size: 14px;
	font-weight: 500;
	color: #5e5873;
}
</style>
